import moment from 'moment';
import { DateTime } from 'luxon';
import { Common, TaskHelpers } from 'ontraccr-common';

import { getIdMap } from '../helpers/helpers';
import { timeIsBetween } from '../helpers/time';
import {
  isGoodTask as isGoodOTTask,
  deriveTasksWithBuckets,
} from '../helpers/overtime/otHelpers';

import { formatProjectLabelFromCompanySettings } from '../projects/projectHelpers';

export default {};

export const filterOnCols = (columns) => (col) => !columns
  || (columns.has && columns.has(col))
  || columns.length === 0 || (columns.includes && columns.includes(col));

export const getUnique = (arr, key) => Array.from(
  new Set(
    arr.map((item) => (item ? item[key] : {})),
  ),
);

export const sortAlphabetically = (ascending = true) => (a, b) => {
  // equal items sort equally
  if (a && b && a.project === b.project) {
    return 0;
  }
  // nulls sort after anything else
  if (a.project === null) {
    return 1;
  }
  if (b.project === null) {
    return -1;
  }
  // otherwise, if we're ascending, lowest sorts first
  if (ascending) {
    return a.project < b.project ? -1 : 1;
  }
  // if descending, highest sorts first

  return a.project < b.project ? 1 : -1;
};

export const toFixed = (val, currency = true) => (val !== null && val !== undefined ? val.toLocaleString(undefined, currency ? { style: 'currency', currency: 'USD' } : {}) : null);

/**
 * Sum hours from costcode category assignments.
 * @param {Object} phase - categoryAssignments[costcodeId][phaseId|'unphased']:
 * costcode phase with categories
 * @returns {number}
 */
const sumHoursFromCostcodePhase = (phase = {}) => (
  Object.values(phase).reduce((sum, a) => sum + (Number(a?.hours) || 0), 0)
);

const sumHoursForPhase = (categoryAssignments = {}, phaseId) => {
  if (!phaseId) return null;
  let total = 0;
  let found = false;
  Object.values(categoryAssignments).forEach((phases) => {
    if (!phases?.[phaseId]) return;
    found = true;
    total += sumHoursFromCostcodePhase(phases[phaseId]);
  });
  return found ? total : null;
};

export const getUnphasedHoursPerProject = ({
  phases = [],
  costcodes = [],
  projects = [],
  allPhases,
  allProjects,
  categoryAssignments = {},
}) => {
  const unphasedHours = {};
  const fullPhaseList = allPhases ?? phases;
  const fullProjectList = allProjects ?? projects;

  fullProjectList.forEach((project) => {
    unphasedHours[project.id] = 0;
  });

  const phasedCostcodeSet = new Set(
    fullPhaseList.map(({ costcodeId }) => costcodeId),
  );
  costcodes.forEach((costcode) => {
    const { id: costcodeId, projectId } = costcode;
    if (phasedCostcodeSet.has(costcodeId) || !projectId) return;
    const unphasedCostcodeCategories = categoryAssignments[costcode.id]?.unphased;
    unphasedHours[projectId] += sumHoursFromCostcodePhase(unphasedCostcodeCategories);
  });
  return unphasedHours;
};

export const getEstimatedHours = ({
  lowestFilter,
  project,
  phase,
  costcode,
  unphasedHours = {},
  phaseId,
  categoryAssignments = {},
}) => {
  if (lowestFilter === 'project') {
    return project ? project.labourHours : 0;
  }

  if (lowestFilter === 'phase') {
    const fromAssignments = sumHoursForPhase(categoryAssignments, phaseId);
    if (fromAssignments !== null) return fromAssignments;
    if (!phase && project?.id && project.id in unphasedHours) {
      return unphasedHours[project.id];
    }
    return 0;
  }

  if (lowestFilter === 'costcode' && costcode?.id in categoryAssignments) {
    const phaseKey = phaseId ?? 'unphased';
    return sumHoursFromCostcodePhase(categoryAssignments[costcode.id]?.[phaseKey]);
  }

  return 0;
};

export const prepareReportData = ({
  costcodes = [],
  projects = [],
  phases = [],
  allCostcodes,
  allPhases,
  allProjects,
}) => {
  const phasedCostcodeSet = new Set();
  const costcodeIdMap = getIdMap(allCostcodes ?? costcodes);
  const projectIdMap = getIdMap(allProjects ?? projects);
  const phaseMap = {};
  // One entry per (costcode, phase) so cost codes in multiple phases get one row per phase
  const costcodePhaseList = [];
  (allPhases ?? phases).forEach(({
    name, description, id, hours = 0, costcodeId,
  }) => {
    if (!(id in phaseMap)) {
      phaseMap[id] = {
        name,
        description,
        hours: hours || 0,
      };
    } else {
      phaseMap[id].hours += hours;
    }
    phasedCostcodeSet.add(costcodeId);
    if (costcodeId in costcodeIdMap) {
      costcodeIdMap[costcodeId].hours = hours;
      costcodeIdMap[costcodeId].phaseId = id;
      costcodePhaseList.push({ costcodeId, phaseId: id });
    }
  });

  const unphasedHours = {};
  costcodes.forEach((costcode) => {
    const { id, projectId, hours } = costcode;
    if (!phasedCostcodeSet.has(id) && projectId) {
      const {
        [projectId]: projectUnphased = 0,
      } = unphasedHours;
      unphasedHours[projectId] = projectUnphased + hours;
    }
  });
  return {
    projectIdMap, phaseMap, costcodeIdMap, unphasedHours, costcodePhaseList,
  };
};

export const getDivision = ({
  userDivisions = {},
  divisionMap = {},
  project = {},
  user = {},
}) => {
  if (project.divisionId in divisionMap) {
    const {
      [project.divisionId]: {
        name,
      } = {},
    } = divisionMap;
    return name;
  }
  if (user.id in userDivisions) {
    const {
      [user.id]: divisions = [],
    } = userDivisions;
    const ourDivision = divisions.find((divisionId) => divisionId in divisionMap);
    const {
      [ourDivision]: {
        name,
      } = {},
    } = divisionMap;
    return name;
  }
  return null;
};

export const getDateKey = (task, format = 'MMM D') => {
  if (!task?.endTime) return '';
  return moment(task.endTime).tz(task.timezone ?? DateTime.local().zoneName).format(format);
};

export const failsFilter = (filters, item, key = 'name', formatter = (x) => x[key]) => {
  if (!item && !filters.has('None')) return true;
  return item && !filters.has(formatter(item));
};

// An empty/undefined filter set means "all cost codes" are selected.
// A non-empty set contains the selected cost code ids (plus 'None' for null cost codes).
export const failsCostcodeFilter = (filters, item) => {
  if (!filters || filters.size === 0) return false;
  if (!item) return !filters.has('None');
  return !filters.has(item.id);
};

export const failsProjectFilter = (filters, item, settings) => {
  if (!item) return !filters.has('None');
  const label = formatProjectLabelFromCompanySettings({
    name: item.name, number: item.number, settings,
  });
  return !filters.has(label);
};

export const isValidTaskHelper = ({
  filters = {},
  dateRange = [],
  projectIdMap = {},
  costcodeIdMap = {},
  phaseMap = {},
  checkPhase,
  workType,
  settings = {},
}) => (task, onlyDates) => {
  const {
    projects: filterProject,
    costcodes: filterCostcodes,
    phases: filterPhases,
  } = filters;

  if (!onlyDates && workType && !workType.has(task.type)) return false;
  const [startDate, endDate] = dateRange;

  if (
    (!task.startTime || !task.endTime)
    && (!task.ptoStartTime || !task.ptoEndTime)
    && (!task.otStartTime || !task.otEndTime)
    && (!task.doubleOTStartTime || !task.doubleOTEndTime)
    && (!task.breakStartTime || !task.breakEndTime)
  ) return false;

  const useRegularTimes = task.endTime && task.startTime;

  const taskEndTime = useRegularTimes ? task.endTime : task.ptoEndTime;
  const taskStartTime = useRegularTimes ? task.startTime : task.ptoStartTime;
  const taskToCheck = {
    ...task,
    endTime: taskEndTime,
    startTime: taskStartTime,
  };

  if (!(timeIsBetween(taskToCheck, startDate, endDate, settings?.useEndDateOvernight))) {
    return false;
  }

  if (onlyDates) return true;

  const project = projectIdMap[task.projectId];
  if (task.projectId && !project) return false; // Not part of our selected divisions
  if (failsProjectFilter(filterProject, project, settings)) return false;

  const costcode = costcodeIdMap[task.costcodeId];
  if (failsCostcodeFilter(filterCostcodes, costcode)) {
    return false;
  }

  if (checkPhase) {
    const phase = phaseMap[task.phaseId];

    if (failsFilter(filterPhases, phase ? { name: phase?.name } : null)) {
      return false;
    }
  }

  return true;
};

export const getProjectNames = (projects, settings) => (
  projects.map((project) => (
    formatProjectLabelFromCompanySettings({
      name: project?.name, number: project?.number, settings,
    })
  ))
);

export const NONE_COSTCODE_OPTION = { value: 'None', label: 'None' };

// The cost code filter is stored as project labels; resolve those back to project ids
// so cost code option lookups can be scoped the same way the report rows are.
// An empty project filter set means all projects (same as cost codes: empty = all).
const getSelectedProjectIds = ({ selectedProjects, allProjects = [], settings }) => {
  if (!selectedProjects || selectedProjects.size === 0) {
    return allProjects.map((project) => project?.id).filter(Boolean);
  }
  const ids = [];
  allProjects.forEach((project) => {
    const label = formatProjectLabelFromCompanySettings({
      name: project.name, number: project.number, settings,
    });
    if (selectedProjects.has(label)) ids.push(project.id);
  });
  return ids;
};

export const buildCostcodeFilterCategories = ({
  projects = [],
  phases = [],
  settings = {},
}) => {
  const categoriesById = {};
  projects.forEach((project) => {
    if (!project?.id || categoriesById[project.id]) return;
    categoriesById[project.id] = {
      id: project.id,
      label: formatProjectLabelFromCompanySettings({
        name: project.name,
        number: project.number,
        settings,
      }),
    };
  });
  const categories = Object.values(categoriesById)
    .sort((a, b) => a.label.localeCompare(b.label));

  const phasesByProject = {};
  const seenPhaseIds = new Set();
  phases.forEach((phase) => {
    if (!phase?.projectId || !phase?.id) return;
    if (seenPhaseIds.has(phase.id)) return;
    seenPhaseIds.add(phase.id);
    if (!(phase.projectId in phasesByProject)) phasesByProject[phase.projectId] = [];
    phasesByProject[phase.projectId].push({
      id: phase.id,
      name: phase.name,
    });
  });

  return categories.map((project) => ({
    ...project,
    phases: (phasesByProject[project.id] || [])
      .sort((a, b) => a.name.localeCompare(b.name)),
  }));
};

export const getCostcodeBucketFetchParams = (bucket) => {
  if (!bucket || bucket.type === 'none') return null;
  if (bucket.type === 'global') {
    return { onlyGlobal: true, onlyActive: true };
  }
  if (bucket.type === 'unphased') {
    return {
      projectIds: [bucket.projectId],
      onlyUnphased: true,
      onlyActive: true,
    };
  }
  if (bucket.type === 'phase') {
    return {
      phaseIds: [bucket.phaseId],
      onlyActive: true,
    };
  }
  return null;
};

export const makeCostcodeSearchParams = ({
  selectedProjects,
  allProjects,
  settings,
  search = '',
}) => {
  const projectIds = getSelectedProjectIds({ selectedProjects, allProjects, settings });
  const params = { search, onlyActive: true };
  if (projectIds.length) {
    params.projectIds = projectIds;
  }
  return params;
};

// Seeds the popup with the currently selected cost codes so they stay visible even
// before/without a search.
export const getCostcodeInitialOptions = (costcodes = [], { includeNone = true } = {}) => {
  const seenIds = new Set();
  const options = [];
  costcodes.filter(Boolean).forEach((cc) => {
    if (!cc?.id || seenIds.has(cc.id)) return;
    seenIds.add(cc.id);
    options.push({ value: cc.id, label: `${cc.code} - ${cc.name}` });
  });
  return includeNone ? [NONE_COSTCODE_OPTION].concat(options) : options;
};

// Column sets for POST /costcodes/byIds (see backend/routes/costcodes/getByIds.js).
// canClockIn is computed server-side from category assignments, not a DB column.
// wageHistory is attached from costcodeWageHistory, not a DB column.
export const COSTCODE_BY_IDS_COLUMNS = {
  DEFAULT: ['id', 'code', 'name'],
  PROJECT_STATUS: ['id', 'code', 'name', 'projectId', 'canClockIn'],
  GANTT: ['id', 'code', 'name', 'projectId', 'canClockIn'],
  PAYROLL: [
    'id',
    'code',
    'name',
    'hourlyWage',
    'dailyWage',
    'wageAdjustment',
    'wageMultiplier',
    'hourlyBillingRate',
    'dailyBillingRate',
    'wageHistory',
  ],
};

export const getReferencedCostcodeIds = (timeEntryUserMap = {}) => {
  const ids = new Set();
  Object.values(timeEntryUserMap).forEach((tasks = []) => {
    (tasks || []).forEach((task) => {
      if (task?.costcodeId) ids.add(task.costcodeId);
    });
  });
  return Array.from(ids);
};

const isGoodTime = (startTime, endTime) => (
  !Common.isNullOrUndefined(startTime)
  && !Common.isNullOrUndefined(endTime)
);

export const isGoodTask = (task) => (
  isGoodTime(task.startTime, task.endTime)
  || isGoodTime(task.otStartTime, task.otEndTime)
  || isGoodTime(task.breakStartTime, task.breakEndTime)
  || isGoodTime(task.doubleOTStartTime, task.doubleOTEndTime)
  || isGoodTime(task.ptoStartTime, task.ptoEndTime)
);

/**
 * Report read path: stored OT segments via deriveBuckets.
 * - manual OT + overallTime + Rounded: day total rounding on last task(s) per day.
 * - manual OT + taskTime + Rounded: task-time day total rounding on last task(s) per day.
 * - automatic OT: rounding is persisted on write; deriveBuckets only.
 * - taskTime + Actual: deriveBuckets on stored startTime/endTime segments.
 * - taskTime + Rounded: deriveBuckets on stored rounded segments.
 */
export const getDerivedTasksInRange = ({
  tasks = [],
  companySettings = {},
  settingsMap = {},
  dateRange,
  shouldIncludeZeroTimePTO = false,
  isRounded = true,
}) => {
  const [startMoment, endMoment] = dateRange ?? [];
  if (!moment.isMoment(startMoment) || !moment.isMoment(endMoment)) return [];

  const startDT = DateTime.fromMillis(startMoment.valueOf()).startOf('day');
  const endDT = DateTime.fromMillis(endMoment.valueOf()).endOf('day');

  const filtered = tasks.filter((task) => {
    if (!isGoodOTTask(task, shouldIncludeZeroTimePTO)) return false;
    const date = TaskHelpers.getTaskDate(task);
    if (!date) return false;
    return date >= startDT && date <= endDT;
  });

  if (!filtered.length) return [];

  const byUser = {};
  filtered.forEach((task) => {
    const { userId } = task;
    if (!byUser[userId]) byUser[userId] = [];
    byUser[userId].push(task);
  });

  const result = [];
  Object.values(byUser).forEach((userTasks) => {
    const settings = settingsMap[userTasks[0].userId] ?? companySettings;
    result.push(...deriveTasksWithBuckets({ tasks: userTasks, settings, isRounded }));
  });

  return result;
};

export const getSavedReportFilterData = ({
  data = [],
  idMap = {},
}) => {
  const seenIds = new Set();
  return data.reduce((acc, id) => {
    if (id === null) {
      acc.hasNull = true;
      return acc;
    }
    if (seenIds.has(id)) return acc;
    const item = idMap?.[id];
    if (item) {
      seenIds.add(id);
      acc.filters.push(item);
    }
    return acc;
  }, { filters: [], hasNull: false });
};

export const getInitialFilters = ({
  selectAllUsers,
  allUsers,
  users,
  selectAllProjects,
  allProjects,
  projects,
  selectAllCostcodes,
  settings,
  costcodes,
  selectAllPhases,
  allPhases,
  phases,
  hasNullProject,
  hasNullPhase,
  hasNullCostcode,
  addUsers = true,
  addProjects = true,
  addPhases = true,
  addCostcodes = true,
}) => {
  const filters = {};
  if (addUsers) {
    filters.users = selectAllUsers ? allUsers : users;
  }
  if (addProjects) {
    const projectFilter = new Set(
      getProjectNames(selectAllProjects ? allProjects : projects, settings),
    );
    if (hasNullProject) {
      projectFilter.delete(null);
      projectFilter.add('None');
    }
    filters.projects = projectFilter;
  }
  if (addPhases) {
    const phaseFilter = new Set(
      getUnique(selectAllPhases ? allPhases : phases, 'name'),
    );
    if (hasNullPhase) {
      phaseFilter.delete(null);
      phaseFilter.add('None');
    }
    filters.phases = phaseFilter;
  }
  if (addCostcodes) {
    const costcodeFilter = new Set();
    if (!selectAllCostcodes) {
      costcodes.forEach((cc) => {
        if (cc?.id) costcodeFilter.add(cc.id);
      });
      if (hasNullCostcode) costcodeFilter.add('None');
    }
    filters.costcodes = costcodeFilter;
  }
  return filters;
};

export const currencyFormatter = (value) => (
  `$${value.toFixed(2)}`
    .replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    .replace((/(?<=\.\d{2}).*/g), '')
);

export const percentFormatter = (value) => `${value.toFixed(2)}%`;
